import React, { useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Toast, ToastDescription, ToastTitle, ToastViewport } from "./ui/toast";
import { useNavigate } from "react-router-dom";

const API_URL = process.env.REACT_APP_API_URL; // Ensure this matches your backend URL

const ReviewPage = () => {
  const username = process.env.REACT_APP_API_USERNAME;
  const password = process.env.REACT_APP_API_PASSWORD;
  const credentials = `${username}:${password}`;
  const encodedCredentials = btoa(credentials); // Base64 encode the credentials

  const navigate = useNavigate();

  const [id, setId] = useState("");
  const [data, setData] = useState(null);
  const [declineReason, setDeclineReason] = useState("");
  const [toastList, setToastList] = useState([]);
  const [showRejectionInput, setShowRejectionInput] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [alert, setAlert] = useState({
    message: "",
    type: "default",
    visible: false,
  });

  const fetchApplicator = async () => {
    if (!id.trim()) {
      setToastList((prev) => [
        ...prev,
        {
          id: Date.now(),
          title: "ERROR!",
          description: "Please enter a valid token.",
          variant: "destructive",
        },
      ]);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/applicator/${id}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Basic ${encodedCredentials}`,
        },
      });

      const result = await response.json();
      if (response.ok) {
        setToastList((prev) => [
          ...prev,
          {
            id: Date.now(),
            title: "SUCCESS!",
            description:
              result.data?.message || result?.error || result?.error || "!",
            variant: "default",
          },
        ]);

        setData(result.data.applicator);
      } else {
        setToastList((prev) => [
          ...prev,
          {
            id: Date.now(),
            title: "ERROR!",
            description:
              result.data?.message ||
              result?.error ||
              "Failed to fetch applicator details.",
            variant: "destructive",
          },
        ]);
      }
    } catch (error) {
      console.error("Error fetching applicator details:", error);
      setToastList((prev) => [
        ...prev,
        {
          id: Date.now(),
          title: "ERROR!",
          description: "An error occurred.",
          variant: "destructive",
        },
      ]);
    }
  };

  const handleApprove = async () => {
    try {
      const response = await fetch(`${API_URL}/api/applicator/review`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Basic ${encodedCredentials}`,
        },
        body: JSON.stringify({
          applicatorToken: id,
          isApproved: true,
        }),
      });

      const responseData = await response.json();

      if (response.ok) {
        setToastList((prev) => [
          ...prev,
          {
            id: Date.now(),
            title: "SUCCESS!",
            description: responseData.data?.message || "Application Approved!",
            variant: "default",
          },
        ]);
        setTimeout(() => {
          navigate("/");
        }, 3000);
      } else {
        setToastList((prev) => [
          ...prev,
          {
            id: Date.now(),
            title: "ERROR!",
            description:
              responseData.data?.message || "Failed to approve application.",
            variant: "destructive",
          },
        ]);
      }
    } catch (error) {
      setToastList((prev) => [
        ...prev,
        {
          id: Date.now(),
          title: "ERROR!",
          description: "An error occurred.",
          variant: "destructive",
        },
      ]);
      console.error("Error approving application:", error);
    }
  };

  const handleRejectionSubmit = async () => {
    if (!declineReason.trim()) {
      setToastList((prev) => [
        ...prev,
        {
          id: Date.now(),
          title: "ERROR!",
          description: "Please provide a reason for rejection.",
          variant: "destructive",
        },
      ]);

      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/applicator/review`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Basic ${encodedCredentials}`,
        },
        body: JSON.stringify({
          applicatorToken: id,
          isApproved: false,
          declineReason,
        }),
      });

      const responseData = await response.json();

      if (response.ok) {
        setToastList((prev) => [
          ...prev,
          {
            id: Date.now(),
            title: "SUCCESS!",
            description: responseData.data?.message || "Application Rejected!",
            variant: "default",
          },
        ]);

        setDeclineReason("");

        setTimeout(() => {
          navigate("/");
        }, 3000);
      } else {
        setToastList((prev) => [
          ...prev,
          {
            id: Date.now(),
            title: "ERROR!",
            description:
              responseData.data?.message || "Failed to reject application.",
            variant: "destructive",
          },
        ]);
      }
    } catch (error) {
      setToastList((prev) => [
        ...prev,
        {
          id: Date.now(),
          title: "ERROR!",
          description: "An error occurred.",
          variant: "destructive",
        },
      ]);
      console.error("Error rejecting application:", error);
    }
  };

  const removeToast = (id) => {
    setToastList((prev) => prev.filter((toast) => toast.id !== id));
  };

  return (
    <div className="tw-min-h-screen tw-px-4 tw-pt-6 tw-flex tw-flex-col tw-justify-center tw-items-center tw-space-y-6">
      <div className="tw-bg-white tw-rounded-lg tw-shadow-md tw-p-6 tw-w-full tw-max-w-4xl">
        <h1 className="tw-text-xl md:tw-text-3xl h-sm:tw-text-2xl tw-font-bold tw-mb-4">
          Review Application
        </h1>

        <div className="tw-grid tw-gap-2 tw-mb-4">
          <Input
            id="applicatorToken"
            value={id}
            onChange={(e) => setId(e.target.value)}
            placeholder="Enter applicator token"
            required
          />
          <Button
            className="tw-bg-[#00B5ED] tw-mt-4 tw-text-white tw-py-2 tw-px-4 tw-rounded-md hover:tw-bg-[#00a3d5]"
            onClick={fetchApplicator}
          >
            Fetch Applicator Details
          </Button>
        </div>

        {data && (
          <div className="tw-space-y-4 tw-font-medium">
            <p className="tw-text-gray-700">
              <span className="tw-font-semibold tw-text-[#00B5ED] tw-mr-2">
                First Name:
              </span>
              {data?.firstName}
            </p>
            <p className="tw-text-gray-700">
              <span className="tw-font-semibold tw-text-[#00B5ED] tw-mr-2">
                Last Name:
              </span>
              {data?.lastName}
            </p>
            <p className="tw-text-gray-700">
              <span className="tw-font-semibold tw-text-[#00B5ED] tw-mr-2">
                Email:
              </span>
              {data?.email}
            </p>
            <p className="tw-text-gray-700">
              <span className="tw-font-semibold tw-text-[#00B5ED] tw-mr-2">
                Company Name:
              </span>
              {data?.companyName}
            </p>
            <p className="tw-text-gray-700">
              <span className="tw-font-semibold tw-text-[#00B5ED] tw-mr-2">
                Company Location:
              </span>
              {data?.companyLocation}
            </p>
            <p className="tw-text-gray-700">
              <span className="tw-font-semibold tw-text-[#00B5ED] tw-mr-2">
                Job Title:
              </span>
              {data?.jobTitle}
            </p>
            <p className="tw-text-gray-700">
              <span className="tw-font-semibold tw-text-[#00B5ED] tw-mr-2">
                Reason for Application:
              </span>
              {data?.reasonForApplication}
            </p>
          </div>
        )}
      </div>

      {data && (
        <div className="tw-flex tw-space-x-4">
          <Button
            size="xl"
            className="tw-bg-green-500 tw-uppercase tw-font-semibold tw-text-white tw-py-3 tw-px-6 tw-rounded-lg hover:tw-bg-green-600"
            onClick={handleApprove}
          >
            Approve
          </Button>
          <Button
            size="xl"
            className="tw-bg-red-500 tw-uppercase tw-font-semibold tw-text-white tw-py-3 tw-px-6 tw-rounded-lg hover:tw-bg-red-600"
            onClick={() => setShowRejectionInput(true)}
          >
            Reject
          </Button>
        </div>
      )}

      {showRejectionInput && (
        <div className="tw-bg-white tw-rounded-lg tw-shadow-md tw-p-6 tw-w-full tw-max-w-2xl tw-mt-6">
          <label
            htmlFor="declineReason"
            className="tw-text-gray-700 tw-font-medium"
          >
            Reason for Rejection:
          </label>
          <textarea
            id="declineReason"
            className="tw-w-full tw-text-black tw-mt-2 tw-p-3 tw-border tw-border-gray-300 tw-rounded-lg focus:tw-outline-none focus:tw-border-blue-500"
            rows="4"
            value={declineReason}
            onChange={(e) => setDeclineReason(e.target.value)}
          />
          <Button
            className="tw-bg-red-500 tw-text-white tw-py-2 tw-px-4 tw-mt-4 tw-rounded-lg hover:tw-bg-blue-600"
            onClick={handleRejectionSubmit}
          >
            Submit Rejection Reason
          </Button>
        </div>
      )}

      {/* Toast Notifications */}
      <ToastViewport />
      {toastList.map(({ id, title, description, variant }) => (
        <Toast
          key={id}
          variant={variant}
          open={true}
          duration={Infinity}
          onOpenChange={(isOpen) => {
            if (!isOpen) removeToast(id);
          }}
          className="tw-flex tw-flex-col tw-text-center"
        >
          <ToastTitle>{title}</ToastTitle>
          <ToastDescription>{description}</ToastDescription>

          {/* Close Button */}
          <button
            onClick={() => removeToast(id)}
            className="tw-mt-3 tw-self-center tw-text-sm tw-font-medium tw-bg-white tw-px-2 tw-py-1 tw-text-black hover:tw-bg-white/80"
          >
            Close
          </button>
        </Toast>
      ))}
    </div>
  );
};

export default ReviewPage;
